import { fetchJson } from "./lib/fetchJson.js";
import {
  armorVariantSelection,
  clampQuantity,
  parseArmorPurchaseConfig,
  type ArmorPurchaseConfig,
} from "./lib/armorPurchase.js";
import { showCartErrorToast } from "./lib/toast.js";
import { showCartSuccessModal } from "./modules/cartSuccessModal.js";

class ArmorPurchase extends HTMLElement {
  private config: ArmorPurchaseConfig | null = null;
  private form: HTMLFormElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private qtyInput: HTMLInputElement | null = null;
  private variantSelect: HTMLSelectElement | null = null;
  private priceEl: HTMLElement | null = null;
  private sellingPlanInput: HTMLInputElement | null = null;
  private productHandle = "";

  connectedCallback() {
    this.config = parseArmorPurchaseConfig(this);
    this.productHandle = this.dataset.productHandle || document.body.dataset.handle || "";
    if (!this.config) return;

    this.form = this.querySelector(".armorPriceRow__form");
    this.submitButton = this.querySelector(".armorPriceRow__submit");
    this.qtyInput = this.querySelector(".armorPriceRow__qtyInput");
    this.variantSelect = this.querySelector('select[name="id"]');
    this.priceEl = this.closest(".armorPriceRow")?.querySelector("[data-armor-price]") ?? null;
    this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');

    this.qtyInput?.addEventListener("input", this.onQuantityInput.bind(this));
    this.qtyInput?.addEventListener("change", this.onQuantityChange.bind(this));
    this.variantSelect?.addEventListener("change", this.onVariantChange.bind(this));
    this.form?.addEventListener("submit", this.onFormSubmit.bind(this));
    this.onVariantChange();
  }

  /** A ?variant= link preselects its option server side, so this also runs on load. */
  private onVariantChange() {
    const option = this.variantSelect?.selectedOptions[0] ?? null;
    const selection = armorVariantSelection(option, this.priceEl?.dataset.fromPrice ?? "");
    if (this.priceEl) this.priceEl.textContent = selection.price;
    if (this.sellingPlanInput) this.sellingPlanInput.value = selection.sellingPlan;
  }

  private getQuantity(): number {
    const min = this.config?.minQuantity ?? 1;
    const raw = this.qtyInput?.value ?? String(min);
    const parsed = parseInt(raw, 10);
    const max = this.config?.maxQuantity ?? null;
    return clampQuantity(parsed, max, min);
  }

  private onQuantityInput() {
    if (!this.qtyInput || !this.config) return;
    const parsed = parseInt(this.qtyInput.value, 10);
    if (!Number.isFinite(parsed)) return;

    const max = this.config.maxQuantity;
    if (max != null && max > 0 && parsed > max) {
      this.qtyInput.value = String(max);
    }
  }

  private onQuantityChange() {
    const quantity = this.getQuantity();
    if (this.qtyInput && this.qtyInput.value !== String(quantity)) {
      this.qtyInput.value = String(quantity);
    }
  }

  private async onFormSubmit(e: SubmitEvent) {
    e.preventDefault();

    const quantity = this.getQuantity();
    if (this.qtyInput && this.qtyInput.value !== String(quantity)) {
      this.qtyInput.value = String(quantity);
    }

    const variantId = parseInt(this.variantSelect?.value || "", 10);
    if (!variantId) return;

    const item: { id: number; quantity: number; selling_plan?: number } = {
      id: variantId,
      quantity,
    };

    const sellingPlanId = parseInt(this.sellingPlanInput?.value || "", 10);
    if (!Number.isNaN(sellingPlanId) && sellingPlanId > 0) {
      item.selling_plan = sellingPlanId;
    }

    this.submitButton?.setAttribute("disabled", "disabled");

    try {
      await fetchJson("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: [item] }),
      });
      showCartSuccessModal(this.productHandle);
    } catch (_error) {
      showCartErrorToast();
    } finally {
      this.submitButton?.removeAttribute("disabled");
    }
  }
}

customElements.define("armor-purchase", ArmorPurchase);
